/**
 * BwC Notes Panel Component
 * Displays all notes for a program participant in a rich card format
 */

import { Icon, Link, mergeStyles, PanelType, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import React from "react";
import CustomPanel from "../../SharedComponents/CustomPanel/CustomPanel";
import { useLazyGetBwCParticipantNotesQuery } from "../../slices/BuildWithCustomer.slice";
import { IBwCNotesPanelProps, IBwCParticipantNote } from "./BuildWithCustomer.interfaces";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";

/**
 * Format date for display
 */
const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
};

// Color palette for note cards
const NOTE_COLORS = [
    { bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", light: "#f0f0ff" },
    { bg: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", light: "#e8f8f0" },
    { bg: "linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)", light: "#fff5eb" },
    { bg: "linear-gradient(135deg, #00b4db 0%, #0083b0 100%)", light: "#e8f7fc" },
    { bg: "linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)", light: "#f0f8f4" }
];

// Constants for content truncation
const COLLAPSED_LINE_HEIGHT = 1.7;
const COLLAPSED_MAX_LINES = 4;
const COLLAPSED_MAX_HEIGHT = COLLAPSED_LINE_HEIGHT * 14 * COLLAPSED_MAX_LINES; // lineHeight * fontSize * lines

/**
 * Single Note Card Component with expandable content
 */
const NoteCard: React.FC<{ note: IBwCParticipantNote; index: number }> = ({ note, index }) => {
    const colorScheme = NOTE_COLORS[index % NOTE_COLORS.length];
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [needsTruncation, setNeedsTruncation] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Check if content needs truncation on mount and when note changes
    React.useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight;
            setNeedsTruncation(scrollHeight > COLLAPSED_MAX_HEIGHT + 10);
        }
    }, [note.notes]);

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const handleCopyNote = async () => {
        const contentToCopy = `Subject: ${note.subject || "N/A"}\n\n${note.notes || "No content"}`;
        try {
            await navigator.clipboard.writeText(contentToCopy);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = contentToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    return (
        <div style={{
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #e1dfdd",
            marginBottom: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
            transition: "box-shadow 0.2s ease"
        }}>
            {/* Header with gradient */}
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                background: colorScheme.bg,
                color: "#ffffff"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700
                    }}>
                        {index + 1}
                    </div>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                            {formatDateTime(note.createdOn)}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>
                            by {note.createdBy || "Unknown"}
                        </div>
                    </div>
                </div>

                {/* Copy Button */}
                <button
                    onClick={handleCopyNote}
                    title={isCopied ? "Copied!" : "Copy note content"}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 12px",
                        background: isCopied
                            ? "rgba(16, 124, 16, 0.9)"
                            : "rgba(255,255,255,0.2)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        outline: "none",
                        backdropFilter: "blur(4px)"
                    }}
                    onMouseEnter={(e) => {
                        if (!isCopied) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.35)";
                            e.currentTarget.style.transform = "scale(1.02)";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isCopied) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                            e.currentTarget.style.transform = "scale(1)";
                        }
                    }}
                >
                    <Icon
                        iconName={isCopied ? "CheckMark" : "Copy"}
                        style={{ fontSize: 14 }}
                    />
                    {isCopied ? "Copied!" : "Copy"}
                </button>
            </div>

            {/* Subject */}
            <div style={{
                padding: "12px 16px",
                background: colorScheme.light,
                borderBottom: "1px solid #e1dfdd"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon iconName="Tag" style={{ fontSize: 14, color: "#0078d4" }} />
                    <Text variant="medium" style={{ fontWeight: 600, color: "#323130" }}>
                        <strong>Subject:</strong> {note.subject?.trim() || <span style={{ color: "#a19f9d", fontStyle: "italic" }}>N/A</span>}
                    </Text>
                </div>
            </div>

            {/* Content with expand/collapse */}
            <div style={{ padding: 16, background: "#ffffff", position: "relative" }}>
                <div
                    ref={contentRef}
                    style={{
                        lineHeight: COLLAPSED_LINE_HEIGHT,
                        color: "#323130",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: 14,
                        maxHeight: isExpanded ? "none" : `${COLLAPSED_MAX_HEIGHT}px`,
                        overflow: "hidden",
                        transition: "max-height 0.3s ease-in-out"
                    }}
                >
                    {note.notes || "No content available"}
                </div>

                {/* View More / View Less Button */}
                {needsTruncation && (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: isExpanded ? 12 : 8,
                        paddingTop: isExpanded ? 0 : 8
                    }}>
                        <button
                            onClick={toggleExpanded}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "8px 16px",
                                background: isExpanded
                                    ? "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)"
                                    : "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
                                color: isExpanded ? "#323130" : "#ffffff",
                                border: "none",
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: isExpanded
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "0 2px 6px rgba(0,120,212,0.3)",
                                transition: "all 0.2s ease",
                                outline: "none"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = isExpanded
                                    ? "0 2px 6px rgba(0,0,0,0.15)"
                                    : "0 4px 10px rgba(0,120,212,0.4)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = isExpanded
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "0 2px 6px rgba(0,120,212,0.3)";
                            }}
                        >
                            <Icon
                                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                                style={{ fontSize: 12 }}
                            />
                            {isExpanded ? "View Less" : "View More"}
                        </button>
                    </div>
                )}
            </div>

            {/* Footer with modification info */}
            <div style={{
                padding: "12px 16px",
                background: "#f8f8f8",
                borderTop: "1px solid #edebe9",
                display: "flex",
                flexDirection: "column",
                gap: 8
            }}>
                {/* Created info */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon iconName="AddFriend" style={{ fontSize: 14, color: "#107c10" }} />
                    <Text variant="small" style={{ color: "#323130" }}>
                        <strong>Created:</strong> {formatDateTime(note.createdOn)} by <strong>{note.createdBy || "Unknown"}</strong>
                        {note.createdByAlias && <span style={{ color: "#8a8886" }}> ({note.createdByAlias})</span>}
                    </Text>
                </div>
                {/* Modified info */}
                {note.modifiedOn && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="Edit" style={{ fontSize: 14, color: "#d83b01" }} />
                        <Text variant="small" style={{ color: "#323130" }}>
                            <strong>Modified:</strong> {formatDateTime(note.modifiedOn)} by <strong>{note.modifiedBy || "Unknown"}</strong>
                            {note.modifiedByAlias && <span style={{ color: "#8a8886" }}> ({note.modifiedByAlias})</span>}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * BwC Notes Panel - Main Component
 */
const BwCNotesPanel: React.FC<IBwCNotesPanelProps> = ({
    isOpen,
    onDismiss,
    programParticipantId,
    customerName,
    programName
}) => {
    const [getNotes, { data, isLoading, error, isFetching }] = useLazyGetBwCParticipantNotesQuery();

    React.useEffect(() => {
        if (isOpen && programParticipantId) {
            getNotes({ programParticipantId }, true);
        }
    }, [isOpen, programParticipantId, getNotes]);

    const renderContent = () => {
        if (error) {
            return <div style={{ marginTop: 30 }}><DisplayAPIsException error={error} height={200} /></div>;
        }

        if (isLoading || isFetching) {
            return (
                <div className={styles.loadingContainer} style={{ marginTop: 40 }}>
                    <Spinner size={SpinnerSize.large} label="Loading notes..." />
                </div>
            );
        }

        if (!data?.notes || data.notes.length === 0) {
            return (
                <div className={styles.emptyState} style={{ marginTop: 40 }}>
                    <Icon iconName="DocumentSearch" style={{ fontSize: 48, color: "#c8c6c4", marginBottom: 16 }} />
                    <Text variant="large" style={{ fontWeight: 600 }}>No Notes Found</Text>
                    <Text variant="medium" style={{ color: "#605e5c", marginTop: 8 }}>
                        No notes recorded for this engagement.
                    </Text>
                </div>
            );
        }

        // Generate CRM URL from participant ID
        const crmUrl = `https://m365crm.crm.dynamics.com/main.aspx?appid=bf381023-a1eb-42ad-b3f5-4ad6a41655fc&pagetype=entityrecord&etn=ems_programparticipents&id=${programParticipantId}`;

        return (
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 40 }}>
                {/* Customer & Program Info */}
                <div style={{
                    padding: 16,
                    background: "#f8f8f8",
                    borderRadius: 8,
                    border: "1px solid #edebe9"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Icon iconName="Contact" style={{ fontSize: 14, color: "#0078d4" }} />
                        <Text variant="medium" style={{ fontWeight: 600 }}>{customerName}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="Org" style={{ fontSize: 14, color: "#605e5c" }} />
                        <Text variant="medium" style={{ color: "#605e5c" }}>{programName}</Text>
                    </div>
                </div>

                {/* Stats bar with CRM and Kusto links */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #e8f4fd 0%, #f0f6ff 100%)",
                    borderRadius: 8,
                    border: "1px solid #d0e0f5"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="Documentation" style={{ fontSize: 18, color: "#0078d4" }} />
                        <Text variant="large" style={{ fontWeight: 700, color: "#0078d4" }}>{data.notes.length}</Text>
                        <Text variant="medium" style={{ color: "#605e5c" }}>Notes</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* CRM Link */}
                        <Link
                            href={crmUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "6px 12px",
                                background: "rgba(0,120,66,0.1)",
                                borderRadius: 4,
                                textDecoration: "none",
                                fontSize: 13,
                                color: "#107c10"
                            }}
                        >
                            <Icon iconName="Link" style={{ fontSize: 12 }} />
                            Open in CRM
                        </Link>
                        {/* Kusto Link */}
                        {data.kustoLink && (
                            <Link
                                href={data.kustoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "6px 12px",
                                    background: "rgba(0,120,212,0.1)",
                                    borderRadius: 4,
                                    textDecoration: "none",
                                    fontSize: 13
                                }}
                            >
                                <Icon iconName="OpenInNewWindow" style={{ fontSize: 12 }} />
                                Explore Kusto
                            </Link>
                        )}
                    </div>
                </div>

                {/* Notes List */}
                <div>
                    {data.notes.map((note, index) => (
                        <NoteCard key={note.notesId || index} note={note} index={index} />
                    ))}
                </div>
            </Stack>
        );
    };

    return (
        <CustomPanel
            dismissPanel={onDismiss}
            enablePanel={isOpen}
            isLoading={false}
            panelTitle="Notes History"
            panelType={PanelType.medium}
            customWidth="550px"
        >
            {renderContent()}
        </CustomPanel>
    );
};

// Styles
const styles = {
    loadingContainer: mergeStyles({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "200px"
    }),
    emptyState: mergeStyles({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 24px",
        textAlign: "center"
    })
};

export default BwCNotesPanel;
